import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API, LoadingScreen } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsPage = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [campaignsData, setCampaignsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("overview"); // overview, campaigns, setup
  const [webhookUrl, setWebhookUrl] = useState(null);

  useEffect(() => {
    Promise.all([
      authAxios.get("/analytics/overview"),
      authAxios.get("/analytics/campaigns")
    ])
      .then(([overviewRes, campaignsRes]) => {
        setAnalytics(overviewRes.data);
        setCampaignsData(campaignsRes.data);
      })
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [authAxios]);

  const loadWebhookInfo = async () => {
    try {
      const res = await authAxios.get("/analytics/webhook-url");
      setWebhookUrl(res.data);
    } catch (err) {
      toast.error("Failed to load webhook info");
    }
  };

  if (loading) return <Layout><LoadingScreen /></Layout>;

  const stats = [
    { label: "Total Campaigns", value: analytics?.total_campaigns || 0, icon: Mail, color: "slate" },
    { label: "Total Subscribers", value: analytics?.total_subscribers || 0, icon: Users, color: "blue" },
    { label: "Emails Sent", value: analytics?.total_sent || 0, icon: Send, color: "emerald" },
    { label: "Emails Opened", value: analytics?.total_opened || 0, icon: MailOpen, color: "amber" },
    { label: "Links Clicked", value: analytics?.total_clicked || 0, icon: MousePointerClick, color: "purple" },
  ];

  // Simple bar chart component
  const MiniChart = ({ data, dataKey, color }) => {
    if (!data || data.length === 0) return null;
    const maxValue = Math.max(...data.map(d => d[dataKey]), 1);
    return (
      <div className="flex items-end gap-0.5 h-16">
        {data.slice(-14).map((d, i) => (
          <div
            key={i}
            className={`flex-1 rounded-t ${color}`}
            style={{ height: `${(d[dataKey] / maxValue) * 100}%`, minHeight: d[dataKey] > 0 ? '4px' : '0' }}
            title={`${d.date}: ${d[dataKey]}`}
          />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="analytics-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Analytics</h1>
            <p className="text-slate-500 mt-1">Track your email campaign performance</p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1">
            <button
              onClick={() => setActiveView("overview")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === "overview" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveView("campaigns")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === "campaigns" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Campaigns
            </button>
            <button
              onClick={() => { setActiveView("setup"); loadWebhookInfo(); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === "setup" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Setup
            </button>
          </div>
        </div>

        {/* Overview View */}
        {activeView === "overview" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {stats.map((stat) => (
                <Card key={stat.label} className="bg-white border-slate-100 rounded-2xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-5 text-center">
                    <div className={`w-12 h-12 rounded-xl bg-${stat.color}-100 flex items-center justify-center mx-auto mb-3`}>
                      <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                    </div>
                    <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              <Card className="bg-white border-slate-100 rounded-2xl">
                <CardHeader>
                  <CardTitle style={{fontFamily: 'Manrope'}}>Open Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <p className="text-6xl font-bold text-emerald-600" style={{fontFamily: 'Manrope'}}>
                      {analytics?.open_rate || 0}%
                    </p>
                    <p className="text-slate-500 mt-2">of emails opened</p>
                    <Progress value={analytics?.open_rate || 0} className="mt-6 h-3" />
                  </div>
                  {campaignsData?.chart_data && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Opens (last 14 days)</p>
                      <MiniChart data={campaignsData.chart_data} dataKey="opened" color="bg-emerald-400" />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-100 rounded-2xl">
                <CardHeader>
                  <CardTitle style={{fontFamily: 'Manrope'}}>Click Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <p className="text-6xl font-bold text-amber-600" style={{fontFamily: 'Manrope'}}>
                      {analytics?.click_rate || 0}%
                    </p>
                    <p className="text-slate-500 mt-2">of links clicked</p>
                    <Progress value={analytics?.click_rate || 0} className="mt-6 h-3" />
                  </div>
                  {campaignsData?.chart_data && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Clicks (last 14 days)</p>
                      <MiniChart data={campaignsData.chart_data} dataKey="clicked" color="bg-amber-400" />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Activity Chart */}
            <Card className="bg-white border-slate-100 rounded-2xl">
              <CardHeader>
                <CardTitle style={{fontFamily: 'Manrope'}}>Email Activity (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                {campaignsData?.chart_data && campaignsData.chart_data.length > 0 ? (
                  <div>
                    <div className="flex items-end gap-1 h-48 px-2">
                      {campaignsData.chart_data.map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                          <div className="w-full flex flex-col gap-0.5">
                            <div
                              className="bg-emerald-400 rounded-t w-full"
                              style={{ height: `${Math.max((d.opened / Math.max(...campaignsData.chart_data.map(x => x.sent || 1), 1)) * 100, d.opened > 0 ? 4 : 0)}px` }}
                              title={`Opened: ${d.opened}`}
                            />
                            <div
                              className="bg-blue-400 w-full"
                              style={{ height: `${Math.max((d.sent / Math.max(...campaignsData.chart_data.map(x => x.sent || 1), 1)) * 100, d.sent > 0 ? 4 : 0)}px` }}
                              title={`Sent: ${d.sent}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2 px-2 text-xs text-slate-400">
                      <span>{campaignsData.chart_data[0]?.date}</span>
                      <span>{campaignsData.chart_data[campaignsData.chart_data.length - 1]?.date}</span>
                    </div>
                    <div className="flex items-center justify-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-400 rounded" />
                        <span className="text-sm text-slate-600">Sent</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-emerald-400 rounded" />
                        <span className="text-sm text-slate-600">Opened</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No activity data yet. Send your first campaign to start tracking!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Campaigns View */}
        {activeView === "campaigns" && (
          <Card className="bg-white border-slate-100 rounded-2xl">
            <CardHeader>
              <CardTitle style={{fontFamily: 'Manrope'}}>Campaign Performance</CardTitle>
              <CardDescription>View detailed analytics for each campaign</CardDescription>
            </CardHeader>
            <CardContent>
              {campaignsData?.campaigns && campaignsData.campaigns.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Campaign</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Recipients</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Sent</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Opens</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Open Rate</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Clicks</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Click Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaignsData.campaigns.map((campaign) => (
                        <tr
                          key={campaign.id}
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/campaign/${campaign.id}`)}
                        >
                          <td className="py-4 px-4">
                            <p className="font-medium text-slate-900">{campaign.name}</p>
                            {campaign.sent_at && (
                              <p className="text-xs text-slate-500">
                                Sent {new Date(campaign.sent_at).toLocaleDateString()}
                              </p>
                            )}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Badge className={`status-${campaign.status}`}>{campaign.status}</Badge>
                          </td>
                          <td className="py-4 px-4 text-center text-slate-700">{campaign.recipients}</td>
                          <td className="py-4 px-4 text-center text-slate-700">{campaign.sent}</td>
                          <td className="py-4 px-4 text-center text-emerald-600 font-medium">{campaign.opened}</td>
                          <td className="py-4 px-4 text-center">
                            <span className={`font-medium ${campaign.open_rate >= 20 ? 'text-emerald-600' : campaign.open_rate >= 10 ? 'text-amber-600' : 'text-slate-500'}`}>
                              {campaign.open_rate}%
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center text-purple-600 font-medium">{campaign.clicked}</td>
                          <td className="py-4 px-4 text-center">
                            <span className={`font-medium ${campaign.click_rate >= 5 ? 'text-emerald-600' : campaign.click_rate >= 2 ? 'text-amber-600' : 'text-slate-500'}`}>
                              {campaign.click_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No campaigns yet. Create your first campaign to see analytics!</p>
                  <Button
                    onClick={() => navigate("/campaign/new")}
                    className="mt-4 bg-afa-navy hover:bg-afa-navy/90 rounded-full"
                  >
                    Create Campaign
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Setup View - Resend Webhook Configuration */}
        {activeView === "setup" && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>
                      Enable Real-Time Tracking
                    </h3>
                    <p className="text-slate-600">
                      Connect Resend's Webhooks to automatically track when recipients open your emails and click links.
                      This takes about 2 minutes to set up.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-100 rounded-2xl">
              <CardHeader>
                <CardTitle style={{fontFamily: 'Manrope'}}>Resend Webhook Setup</CardTitle>
                <CardDescription>Follow these steps to enable email tracking</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {webhookUrl ? (
                  <>
                    {/* Webhook URL */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Your Webhook URL</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={webhookUrl.webhook_url}
                          readOnly
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(webhookUrl.webhook_url);
                            toast.success("URL copied!");
                          }}
                          className="flex-shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-900">Setup Instructions:</h4>
                      <ol className="space-y-3">
                        {webhookUrl.instructions.map((step, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="w-6 h-6 bg-afa-navy text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                              {i + 1}
                            </span>
                            <span className="text-slate-700">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Events to Track */}
                    <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                      <h4 className="font-semibold text-emerald-800 mb-2">Events to Subscribe To:</h4>
                      <div className="flex flex-wrap gap-2">
                        {["email.delivered", "email.opened", "email.clicked", "email.bounced", "email.complained"].map(event => (
                          <Badge key={event} className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            {event}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                    <p className="text-slate-500 mt-2">Loading webhook information...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Help Card */}
            <Card className="bg-amber-50 border-amber-100 rounded-2xl">
              <CardContent className="p-5 flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800 mb-1">Need Help?</h4>
                  <p className="text-sm text-amber-700">
                    If you're not sure how to access your Resend webhooks, check out the
                    <a href="https://resend.com/docs/dashboard/webhooks/introduction"
                       target="_blank" rel="noopener noreferrer"
                       className="underline ml-1">
                      Resend Webhooks documentation
                    </a>.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Onboarding Wizard — first-run experience for fresh SaaS signups

export default AnalyticsPage;
